import { ReactElement, useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Formik, FormikHelpers } from 'formik'
import { Amplify } from 'aws-amplify'
import { resetPassword } from '@aws-amplify/auth'
import { Button, PageTitle, SkipLink } from '../../components/atoms'
import logo from '../../assets/images/logo.svg'
import { Modal } from '../../components/molecules'
import { toast } from 'react-toastify'
import ControlHighlight from '../../components/molecules/KeyboardControls/ControlHighlight'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import KeyboardControl from '../../components/molecules/KeyboardControls/KeyboardControl'
import { HighlightEffect } from '../../enums/KeyboardControls'
import ContactLinks from '../../components/molecules/ContactLinks/ContactLinks'
import { logger } from '../../utils'

export const pageTitle = 'Password Reset - Check your email'
export const idPasswordResetPageMainContent = 'password-reset-page-main-content'

export enum FocusableElement {
  ButtonResend = 'button-resend',
  LinkContact = 'link-contact',
}

function PasswordReset (): ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const keyboardControls = useKeyboardControls()

  const emailRef = useRef<string | undefined>(
    location.state?.email || localStorage.getItem('resetPasswordEmail') || undefined
  )

  // Only redirect if no email AND it's not a hash navigation (skip link click)
  useEffect(() => {
    if (emailRef.current) {
      localStorage.setItem('resetPasswordEmail', emailRef.current)
    } else if (!window.location.hash) {
      // If no email in ref or localStorage, and no hash, redirect
      navigate('/')
    }
  }, [navigate])

  const handleResendEmail = async (): Promise<void> => {
    const email = emailRef.current
    if (!email) {
      navigate('/')
      return
    }
    try {
      setIsSubmitting(true)
      const currentConfig = Amplify.getConfig()
      logger.log('Full Amplify configuration:')
      logger.log(JSON.stringify(currentConfig))
      const result = await resetPassword({ username: email })
      logger.log('Password reset request result:')
      logger.log(JSON.stringify(result))
      toast.success('Password reset email sent successfully', { position: toast.POSITION.TOP_CENTER })
    } catch (err: any) {
      logger.error('Error resending password reset email:')
      logger.error(err)
      toast.error('Failed to resend password reset email. Please try again.', { position: toast.POSITION.TOP_CENTER })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='bg-black min-h-screen font-primary items-center flex justify-center'>
      <SkipLink targetId={idPasswordResetPageMainContent} />
      <header className='absolute left-[40px] top-[40px]'>
        <img src={logo} className='w-[138px]' alt='logo' />
      </header>
      <main
        id={idPasswordResetPageMainContent}
        tabIndex={-1}
      >
        <section>
          <Formik
            initialValues={{}}
            onSubmit={async (_values, actions: FormikHelpers<{}>): Promise<void> => {
              await handleResendEmail()
              actions.setSubmitting(false)
            }}
          >
            {() => (
              <Modal tall>
                <div className='flex flex-col h-full w-full'>
                  <PageTitle title={pageTitle} addScreenReaderH1={false} />
                  <h1 className='font-bold capitalize text-[26px] text-center'>
                    password reset
                  </h1>
                  <div className='flex-1 flex flex-col justify-center items-center overflow-y-auto'>
                    <p className='pt-14 pb-16 font-bold text-[38px] leading-none min-h-1024:pt-20 min-h-1024:pb-32'>
                      Check your email
                    </p>
                    <p className='text-[26px] w-[430px] leading-[3rem] font-thin text-center'>
                      Click the button in the email sent to{' '}
                      <span className='text-dopage-darkGreen'>{emailRef.current}</span>
                      <br />
                      <span className='sr-only'> </span>
                      to reset your password.
                      <br />
                      <br />
                      <br />
                    </p>
                  </div>
                  <div className='max-w-[440px] w-full flex-shrink-0 pb-4 mx-auto'>
                    <ControlHighlight
                      active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonResend)}
                      highlightEffect={HighlightEffect.ButtonHexOutline}
                      data-testid={`highlight-${FocusableElement.ButtonResend}`}
                    >
                      <KeyboardControl>
                        <Button
                          className='font-primary bg-dopage-primary w-full h-[86px] diamond-btn font-bold mt-[40px] flex justify-center items-center'
                          data-testid={FocusableElement.ButtonResend}
                          disabled={isSubmitting}
                          id={FocusableElement.ButtonResend}
                          loading={isSubmitting}
                          onClick={handleResendEmail}
                        >
                          <span className='text-[26px] text-neutral-090'>
                            Resend Email
                          </span>
                        </Button>
                      </KeyboardControl>
                    </ControlHighlight>
                    <ContactLinks
                      id={FocusableElement.LinkContact}
                    />
                  </div>
                </div>
              </Modal>
            )}
          </Formik>
        </section>
      </main>
      <footer className='sr-only' />
    </div>
  )
}

export default PasswordReset
